import { saveAs } from 'file-saver';
import * as R from 'ramda';

import {
  convertPostgrestDateStringToLocalTime,
  convertToPostgrestDateString,
  fetchEngagementSummaryReport,
  fetchEngagementTrendReport,
  fetchListenerDistributionReport,
  fetchOrganizationMembershipSummary,
  fetchReportingConfig,
  fetchTopEpisodesReport,
  fetchTopListenersReport,
  listenerDetailReportCsv,
  listenerSummaryReportCsv,
} from '@/requests';
import { ComplianceType, DateRange } from '@/types';
import { camelCaseKeys } from '@/utils';

import { episodesApi } from '../episodes-api';
import { postgrestApi } from '../postgrest-api';
import { rtkQueryError } from '../store.utils';
import {
  FetchEngagementSummaryReportResponse,
  FetchLearningTrendResponse,
  FetchListenerDistributionResponse,
  FetchOrganizationMembershipSummaryResponse,
  FetchReportingConfigResponse,
  FetchTopEpisodesResponse,
  FetchTopListenersResponse,
} from './member-reporting-api.types';

export const memberReportingApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchReportingConfig: builder.query<FetchReportingConfigResponse, void>({
      query: fetchReportingConfig,
      transformResponse: (response: unknown) => {
        const data = camelCaseKeys(response);
        return {
          minDate: convertPostgrestDateStringToLocalTime(data.minDate),
          maxDate: convertPostgrestDateStringToLocalTime(data.maxDate),
        } as FetchReportingConfigResponse;
      },
    }),
    fetchOrganizationMembershipSummary: builder.query<
      FetchOrganizationMembershipSummaryResponse,
      void
    >({
      query: fetchOrganizationMembershipSummary,
      transformResponse: (response: any) => {
        const data = camelCaseKeys(response);
        return {
          ...data,
          totalNonRemovedMembers: data?.acceptedInvites?.totalNonRemovedMembers,
          acceptedInvites: data?.acceptedInvites?.acceptedInvites,
          engagedActiveMembers:
            data?.engagedActiveMembers?.engagedActiveMembers,
        } as FetchOrganizationMembershipSummaryResponse;
      },
    }),
    fetchEngagementSummaryReport: builder.query<
      FetchEngagementSummaryReportResponse,
      { dateRange: Partial<DateRange> }
    >({
      query: fetchEngagementSummaryReport,
      transformResponse: camelCaseKeys,
    }),
    fetchListenerDistributionReport: builder.query<
      FetchListenerDistributionResponse,
      { dateRange: Partial<DateRange> }
    >({
      query: fetchListenerDistributionReport,
      transformResponse: (response: unknown) => {
        const data = camelCaseKeys(response);

        return data.map((item: any) => ({
          episodeQuantityBucket: item.episodeRange,
          percentageOfTotalUsers: item.pctListeners,
          users: item.listenerCount,
        }));
      },
    }),
    downloadListenerDistributionReportCsv: builder.mutation<
      boolean,
      { dateRange: Partial<DateRange> }
    >({
      queryFn: async ({ dateRange }, _, __, baseQuery) => {
        try {
          const { data, error } = await baseQuery(
            listenerSummaryReportCsv({ dateRange }),
          );

          if (error) return { error };

          if (data)
            saveAs(
              data as Blob,
              `Listener Summary Report (${convertToPostgrestDateString(dateRange.fromDate)} to ${convertToPostgrestDateString(dateRange.toDate)}).csv`,
            );

          return { data: true };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
    fetchLearningTrendReport: builder.query<
      FetchLearningTrendResponse,
      { dateRange: Partial<DateRange> }
    >({
      query: fetchEngagementTrendReport,
      transformResponse: (response: unknown) => {
        const data = camelCaseKeys(response);

        return data.map((item: any) => ({
          date: convertPostgrestDateStringToLocalTime(item?.periodStart),
          users: item?.activeUsers,
          completedEpisodes: item?.completedEpisodes,
          redeemedEpisodes: item?.redeemedEpisodes,
          complianceEarnedEpisodes: item?.complianceEarnedEpisodes,
          startedEpisodes: item?.startedEpisodes,
          grainSize: item?.rollupGrain,
        }));
      },
    }),
    downloadLearningTrendReportCsv: builder.mutation<
      boolean,
      { dateRange: Partial<DateRange> }
    >({
      queryFn: async ({ dateRange }, _, __, baseQuery) => {
        try {
          const { data, error } = await baseQuery(
            listenerDetailReportCsv({ dateRange }),
          );

          if (error) return { error };

          if (data)
            saveAs(
              data as Blob,
              `Listener Detail Report (${convertToPostgrestDateString(dateRange.fromDate)} to ${convertToPostgrestDateString(dateRange.toDate)}).csv`,
            );

          return { data: true };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
    fetchTopEpisodesReport: builder.query<
      FetchTopEpisodesResponse,
      {
        dateRange: Partial<DateRange>;
        userCompliance: ComplianceType;
      }
    >({
      queryFn: async (
        { dateRange, userCompliance },
        { dispatch },
        _,
        baseQuery,
      ) => {
        try {
          const topEpisodesReportResult = (await baseQuery(
            fetchTopEpisodesReport({ dateRange }),
          )) as {
            data: any[];
            error: any;
          };

          if (topEpisodesReportResult.error)
            return { error: topEpisodesReportResult.error };

          const topEpisodesReport = topEpisodesReportResult.data.map(
            camelCaseKeys<any>,
          );

          const episodesResult = (await dispatch(
            episodesApi.endpoints.fetchEpisodes.initiate({
              episodeIds: topEpisodesReport.map(data => data.episodeId),
              compliance: userCompliance,
              includeArchived: true,
            }),
          )) as {
            data: any[];
            error: any;
          };

          if (episodesResult.error) return { error: episodesResult.error };

          const episodeInfoMap = R.indexBy(
            R.prop('episodeId'),
            episodesResult.data,
          );
          const topEpisodesReportMap = R.indexBy(
            R.prop('episodeId'),
            topEpisodesReport,
          );

          const mergedData = Object.values(topEpisodesReportMap)
            .map(data => ({
              ...data,
              episode: episodeInfoMap[data.episodeId],
            }))
            .filter(data => data.episode !== undefined);

          return { data: mergedData as FetchTopEpisodesResponse };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
    fetchTopListenersReport: builder.query<
      FetchTopListenersResponse,
      { dateRange: Partial<DateRange> }
    >({
      query: fetchTopListenersReport,
      transformResponse: (response: unknown) => {
        const data = camelCaseKeys(response);
        return data.map((item: any) => ({
          ...item,
          email: item.organizationEmail,
        }));
      },
    }),
  }),
});

export const {
  useFetchReportingConfigQuery,
  useFetchOrganizationMembershipSummaryQuery,
  useFetchEngagementSummaryReportQuery,
  useFetchListenerDistributionReportQuery,
  useDownloadListenerDistributionReportCsvMutation,
  useFetchLearningTrendReportQuery,
  useDownloadLearningTrendReportCsvMutation,
  useFetchTopListenersReportQuery,
  useFetchTopEpisodesReportQuery,
} = memberReportingApi;
